/**
 * Username sanitization — the SINGLE source of truth shared by the client
 * (live preview / pre-validation) and the server (authoritative enforcement).
 * Both sides running the EXACT same rules is what makes a special-character
 * handle (registered-mark, accents, emoji, CJK) store and render identically
 * everywhere, instead of "fine on one screen, tofu box on another".
 *
 * Policy (decided 2026-06-30): maximize creative freedom — emoji, accents,
 * symbols, any script are all allowed — while stripping ONLY the genuinely
 * harmful invisibles that enable impersonation or break layout:
 *   - NFC-normalize first, so an accented letter is one canonical code point.
 *   - drop C0/C1 control characters and DEL.
 *   - drop invisible spacers used to forge duplicate names (ZERO WIDTH SPACE,
 *     WORD JOINER, BOM, the invisible-maths operators, soft hyphen, LRM/RLM).
 *     ZERO WIDTH JOINER (U+200D) and NON-JOINER (U+200C) are intentionally
 *     KEPT — they're load-bearing inside emoji sequences (flags, families) and
 *     in legitimate Persian/Arabic/Indic text shaping.
 *   - drop bidirectional embedding / override / isolate controls — they
 *     reorder text for spoofing and never belong in a display name.
 *   - collapse every whitespace run to a single ASCII space, trim the ends.
 *   - anti-zalgo: cap a run of combining marks so stacked diacritics can't
 *     bleed over neighbouring UI.
 *   - truncate to MAX_GRAPHEMES by GRAPHEME, so an emoji counts as one and is
 *     never cut through its surrogate pair (the lone-surrogate box bug — a
 *     UTF-16 .slice() is exactly how Phone Dice corrupted names at storage).
 *
 * Every range below is built from ASCII-only \\u escapes via the RegExp
 * constructor on purpose: it keeps this source free of literal invisible bytes
 * (which are unreviewable and broke the parser when pasted inline).
 */
export const USERNAME_MAX_GRAPHEMES = 20;
export const USERNAME_MIN_GRAPHEMES = 1;
// C0 + C1 control characters and DEL.
const CONTROL = new RegExp('[\\u0000-\\u001F\\u007F-\\u009F]', 'g');
// Invisible spacers safe to strip. NOTE: U+200C / U+200D (ZWNJ / ZWJ) are
// deliberately NOT in this set; they are needed by emoji sequences and scripts.
const INVISIBLE = new RegExp('[\\u00AD\\u061C\\u200B\\u200E\\u200F\\u2060\\u2061\\u2062\\u2063\\u2064\\uFEFF]', 'g');
// Bidirectional embedding / override / isolate controls.
const BIDI = new RegExp('[\\u202A-\\u202E\\u2066-\\u2069]', 'g');
// Any whitespace, including the exotic Unicode spaces and line/para separators.
const WHITESPACE = new RegExp('[\\s\\u00A0\\u1680\\u2000-\\u200A\\u2028\\u2029\\u202F\\u205F\\u3000]+', 'g');
// A single combining mark (drives the anti-zalgo cap). RegExp-constructor form
// so TS never static-checks the Unicode property escape against the target.
const COMBINING_MARK = new RegExp('\\p{M}', 'u');
/** Split into user-perceived characters (graphemes), emoji-safe. */
function splitGraphemes(s) {
    // Intl.Segmenter is the correct grapheme splitter (handles ZWJ emoji, flags,
    // skin-tone modifiers). Available in modern Node + WebView; fall back to
    // code-point iteration (still surrogate-safe) where it isn't present.
    const Seg = (typeof Intl !== 'undefined' && Intl.Segmenter) || undefined;
    if (Seg) {
        const seg = new Seg(undefined, { granularity: 'grapheme' });
        const out = [];
        for (const piece of seg.segment(s))
            out.push(piece.segment);
        return out;
    }
    return Array.from(s); // iterates by code point — never splits a surrogate pair
}
/** Count user-perceived characters (graphemes), emoji-safe. */
export function graphemeLength(s) {
    return splitGraphemes(s).length;
}
/**
 * True if `name` collides with our auto-assigned default handle shape
 * (`Player<digits>`, e.g. "Player5994"). Players must not be able to claim one
 * as a custom name — it would let them impersonate a fresh/anonymous account.
 */
export function isReservedUsername(name) {
    return /^player\d+$/i.test(name.trim());
}
/** Keep each grapheme's base char plus at most `max` combining marks. */
function capCombiningMarks(graphemes, max = 2) {
    return graphemes.map((g) => {
        let marks = 0;
        let out = '';
        for (const cp of Array.from(g)) {
            if (COMBINING_MARK.test(cp)) {
                if (marks >= max)
                    continue;
                marks++;
            }
            out += cp;
        }
        return out;
    });
}
/**
 * Clean a raw handle into its canonical, render-safe form. Returns
 * `valid: false` only when nothing usable survives (empty / whitespace /
 * pure-control input) — otherwise the cleaned (possibly truncated) name.
 */
export function sanitizeUsername(raw) {
    if (typeof raw !== 'string')
        return { username: '', valid: false, reason: 'empty' };
    let s = raw.normalize('NFC');
    s = s.replace(CONTROL, '').replace(INVISIBLE, '').replace(BIDI, '');
    s = s.replace(WHITESPACE, ' ').trim();
    if (s.length === 0)
        return { username: '', valid: false, reason: 'empty' };
    let graphemes = capCombiningMarks(splitGraphemes(s));
    if (graphemes.length > USERNAME_MAX_GRAPHEMES) {
        graphemes = graphemes.slice(0, USERNAME_MAX_GRAPHEMES);
    }
    const username = graphemes.join('').trim();
    if (graphemeLength(username) < USERNAME_MIN_GRAPHEMES) {
        return { username: '', valid: false, reason: 'empty' };
    }
    return { username, valid: true };
}
//# sourceMappingURL=username.js.map